/**
 * Spec Phase 6 Slice 6F — IndexedDB-backed offline queue for
 * paragraph auto-save.
 *
 * When `_persistParagraphNow` (specStore.ts) fails with a network
 * error, the pending mutation is enqueued here keyed by
 * paragraph_id. Subsequent successful PATCHes drain the queue
 * one-by-one; on `online` window events or a 30-second polling
 * timer the store also attempts a drain.
 *
 * Schema mirrors `idbUndoStack.ts`: one record (out-of-line key
 * "singleton") holding a Record<paragraphId, QueueEntry>. The
 * map shape matches what the store reads/writes — per-paragraph
 * addressing isn't useful in practice (we always rewrite the
 * whole map after a drain).
 *
 * Conflict policy (per brief §10): last-write-wins per paragraph.
 * If the same paragraph is enqueued multiple times, the most
 * recent entry's body replaces the older one (queued_at is
 * surfaced for audit).
 *
 * Two-tab last-write-wins (brief §10) is OUT OF SCOPE for v1 of
 * this slice — the brief framed it as Phase 6 work; v1 ships
 * pure offline queue + reconnect-flush. Two-tab conflict
 * detection requires `updated_at` echo + 409 Conflict semantics
 * the backend doesn't yet emit; documented as a deviation in the
 * Phase 6 report.
 */

export interface SpecOfflineQueueEntry {
  matterId: string;
  sectionId: string;
  paragraphId: string;
  bodyHtml: string;
  bodyText: string;
  queuedAt: string; // ISO timestamp
}

export type SpecOfflineQueue = Record<string, SpecOfflineQueueEntry>;

const _DB_NAME = 'claimstudio_spec';
const _DB_VERSION = 1;
const _STORE_NAME = 'offline_queue';
const _SINGLETON_KEY = 'singleton';

function _isIDBAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

async function _open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(_DB_NAME, _DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(_STORE_NAME)) {
        db.createObjectStore(_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

export async function loadOfflineQueue(): Promise<SpecOfflineQueue> {
  if (!_isIDBAvailable()) return {};
  let db: IDBDatabase | null = null;
  try {
    db = await _open();
  } catch {
    return {};
  }
  try {
    return await new Promise<SpecOfflineQueue>((resolve, reject) => {
      const tx = db!.transaction(_STORE_NAME, 'readonly');
      const store = tx.objectStore(_STORE_NAME);
      const req = store.get(_SINGLETON_KEY);
      req.onsuccess = () => {
        const v = req.result as SpecOfflineQueue | undefined;
        resolve(v || {});
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return {};
  } finally {
    db.close();
  }
}

export async function persistOfflineQueue(
  queue: SpecOfflineQueue,
): Promise<void> {
  if (!_isIDBAvailable()) return;
  let db: IDBDatabase | null = null;
  try {
    db = await _open();
  } catch {
    return; // best-effort
  }
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(_STORE_NAME, 'readwrite');
      const store = tx.objectStore(_STORE_NAME);
      const req = store.put(queue, _SINGLETON_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    /* best-effort persist */
  } finally {
    db.close();
  }
}

/** Helper used by the store: append/replace one entry, persist. */
export async function enqueueOfflineEntry(
  entry: SpecOfflineQueueEntry,
): Promise<SpecOfflineQueue> {
  const cur = await loadOfflineQueue();
  // Last-write-wins per paragraph.
  cur[entry.paragraphId] = entry;
  await persistOfflineQueue(cur);
  return cur;
}

export async function removeOfflineEntries(
  paragraphIds: string[],
): Promise<SpecOfflineQueue> {
  const cur = await loadOfflineQueue();
  for (const pid of paragraphIds) {
    delete cur[pid];
  }
  await persistOfflineQueue(cur);
  return cur;
}

export async function clearOfflineQueue(): Promise<void> {
  await persistOfflineQueue({});
}
